import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FaEllipsisV } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import "./PharmacyImport.css";
import { apiUrl } from "../../config/appConfig";

const googleLibraries = ["places", "marker"];

const PharmacyImport = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: googleLibraries,
  });

  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  const defaultCenter = { lat: 33.3152, lng: 44.3661 }; // Baghdad, Iraq
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);

  // Fetch all imported pharmacies
  const fetchPharmacies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        apiUrl('/imported-pharmacies'),
        {
          headers: { accept: "application/json" },
        }
      );

      if (response.data.success && response.data.data) {
        // Filter out pharmacies that are removed/hidden and have valid coordinates
        const validPharmacies = response.data.data.filter(
          (pharmacy) => 
            pharmacy.latitude && 
            pharmacy.longitude && 
            !pharmacy.is_removed && 
            !pharmacy.is_hidden
        );
        setPharmacies(validPharmacies);

        // Adjust map center and zoom to show all pharmacies
        if (validPharmacies.length > 0) {
          adjustMapBounds(validPharmacies);
        }
      }
    } catch (error) {
      console.error("Error fetching pharmacies:", error);
      toast.error("Failed to load pharmacies");
    } finally {
      setLoading(false);
    }
  };

  // Adjust map bounds to show all pharmacies
  const adjustMapBounds = (pharmaciesList) => {
    if (pharmaciesList.length === 0) return;

    if (pharmaciesList.length === 1) {
      setMapCenter({
        lat: pharmaciesList[0].latitude,
        lng: pharmaciesList[0].longitude,
      });
      setMapZoom(15);
      return;
    }

    // Calculate bounds
    const lats = pharmaciesList.map((p) => p.latitude);
    const lngs = pharmaciesList.map((p) => p.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Center point
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    setMapCenter({ lat: centerLat, lng: centerLng });

    // Calculate zoom level based on bounds
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom = 10;
    if (maxDiff > 0.1) zoom = 8;
    else if (maxDiff > 0.05) zoom = 9;
    else if (maxDiff > 0.01) zoom = 11;
    else if (maxDiff > 0.005) zoom = 12;
    else zoom = 13;

    setMapZoom(zoom);
  };

  // Handle CSV file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        apiUrl('/imported-pharmacies/import'),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success(
          `Successfully imported ${response.data.data.length} pharmacy(ies)`
        );
        // Refresh the list
        await fetchPharmacies();
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        toast.error(response.data.message || "Failed to import CSV");
      }
    } catch (error) {
      console.error("Error uploading CSV:", error);
      toast.error(
        error.response?.data?.message || "Failed to import CSV file"
      );
    } finally {
      setUploading(false);
    }
  };

  // Handle marker click
  const handleMarkerClick = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
  };

  // Function to restore fullscreen control accessibility
  const restoreFullscreenControlAccess = () => {
    try {
      const fullscreenControls = document.querySelectorAll('.gm-fullscreen-control');
      fullscreenControls.forEach((control) => {
        if (control) {
          // Only update styles, don't interfere with event handlers
          const currentDisplay = window.getComputedStyle(control).display;
          if (currentDisplay !== 'none') {
            // Force reset styles
            control.style.zIndex = '999999';
            control.style.pointerEvents = 'auto';
            control.style.position = 'absolute';
            control.style.right = '10px';
            control.style.top = '10px';
            control.style.left = 'auto';
            control.style.marginLeft = '0';
            
            const button = control.querySelector('button');
            if (button) {
              button.style.zIndex = '999999';
              button.style.pointerEvents = 'auto';
              button.style.cursor = 'pointer';
              button.style.position = 'relative';
            }
          }
        }
      });
    } catch (error) {
      // Silently handle any errors to avoid breaking the UI
      console.warn('Error restoring fullscreen control:', error);
    }
  };

  // Close info window
  const handleInfoWindowClose = () => {
    setSelectedPharmacy(null);
    setShowMenu(false);
    setMenuPosition({ top: 0, left: 0 });
    
    // Ensure fullscreen control is clickable after closing InfoWindow
    setTimeout(() => {
      restoreFullscreenControlAccess();
    }, 100);
    
    // Also restore after a longer delay to catch any async updates
    setTimeout(() => {
      restoreFullscreenControlAccess();
    }, 500);
  };

  // Get the correct portal target (fullscreen element or document.body)
  const getPortalTarget = () => {
    // Check if we're in fullscreen mode
    const fullscreenElement = 
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;
    
    return fullscreenElement || document.body;
  };

  // Handle menu toggle
  const handleMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const willShowMenu = !showMenu;
    
    if (willShowMenu && menuRef.current) {
      // Calculate position for dropdown - align right edge of dropdown with right edge of button
      const buttonRect = menuRef.current.getBoundingClientRect();
      const dropdownWidth = 160; // minWidth of dropdown
      setMenuPosition({
        top: buttonRect.bottom + 4,
        left: buttonRect.right - dropdownWidth // Align dropdown right edge with button right edge
      });
      // Use setTimeout to ensure position is set before showing menu
      setTimeout(() => {
        setShowMenu(true);
      }, 0);
    } else {
      setShowMenu(false);
    }
  };

  // Handle remove from menu
  const handleRemoveFromMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedPharmacy) {
      handleRemovePharmacy(selectedPharmacy.id);
      setShowMenu(false);
      // Ensure fullscreen control is clickable after closing menu
      setTimeout(() => {
        try {
          restoreFullscreenControlAccess();
        } catch (error) {
          console.warn('Error restoring fullscreen control after menu close:', error);
        }
      }, 100);
    }
  };

  // Close menu when clicking outside or when pharmacy changes
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    // Handle fullscreen changes to recalculate position
    const handleFullscreenChange = () => {
      // Small delay to ensure fullscreen transition is complete
      setTimeout(() => {
        if (showMenu && menuRef.current) {
          const buttonRect = menuRef.current.getBoundingClientRect();
          const dropdownWidth = 160;
          setMenuPosition({
            top: buttonRect.bottom + 4,
            left: buttonRect.right - dropdownWidth
          });
        }
      }, 100);
    };

    // Use setTimeout to avoid immediate closure when menu opens
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [showMenu]);

  // Reset menu when selected pharmacy changes
  useEffect(() => {
    setShowMenu(false);
  }, [selectedPharmacy]);

  // Handle fullscreen exit to restore fullscreen control
  useEffect(() => {
    const restoreFullscreenControl = () => {
      // Wait a bit for the fullscreen transition to complete
      setTimeout(() => {
        try {
          const fullscreenControls = document.querySelectorAll('.gm-fullscreen-control');
          fullscreenControls.forEach((control) => {
            if (control) {
              // Only update styles, don't interfere with event handlers
              const currentDisplay = window.getComputedStyle(control).display;
              if (currentDisplay !== 'none') {
                // Update styles without using cssText to avoid breaking event handlers
                control.style.zIndex = '999999';
                control.style.pointerEvents = 'auto';
                control.style.position = 'absolute';
                control.style.right = '10px';
                control.style.top = '10px';
                control.style.left = 'auto';
                control.style.marginLeft = '0';
                
                const button = control.querySelector('button');
                if (button) {
                  button.style.zIndex = '999999';
                  button.style.pointerEvents = 'auto';
                  button.style.cursor = 'pointer';
                  button.style.position = 'relative';
                }
              }
            }
          });
        } catch (error) {
          // Silently handle any errors
          console.warn('Error restoring fullscreen control after exit:', error);
        }
      }, 200);
    };

    const handleFullscreenExit = () => {
      // Check if we actually exited fullscreen (not entered)
      const isFullscreen = 
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      
      if (!isFullscreen) {
        restoreFullscreenControl();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenExit);
    document.addEventListener('webkitfullscreenchange', handleFullscreenExit);
    document.addEventListener('mozfullscreenchange', handleFullscreenExit);
    document.addEventListener('MSFullscreenChange', handleFullscreenExit);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenExit);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenExit);
      document.removeEventListener('mozfullscreenchange', handleFullscreenExit);
      document.removeEventListener('MSFullscreenChange', handleFullscreenExit);
    };
  }, []);

  // Handle remove pharmacy from map
  const handleRemovePharmacy = async (pharmacyId) => {
    if (!pharmacyId) return;

    try {
      // Call API to mark pharmacy as removed/hidden
      const response = await axios.patch(
        apiUrl(`/imported-pharmacies/${pharmacyId}`),
        { action: 'remove' },
        {
          headers: { accept: "application/json" },
        }
      );

      if (response.data.success) {
        // Remove from local state immediately
        setPharmacies((prevPharmacies) =>
          prevPharmacies.filter((pharmacy) => pharmacy.id !== pharmacyId)
        );
        
        // Close the info window
        setSelectedPharmacy(null);
        
        toast.success("Pharmacy location removed from map");
        
        // Adjust map bounds if needed
        const remainingPharmacies = pharmacies.filter(
          (pharmacy) => pharmacy.id !== pharmacyId
        );
        if (remainingPharmacies.length > 0) {
          adjustMapBounds(remainingPharmacies);
        }
      } else {
        toast.error(response.data.message || "Failed to remove pharmacy");
      }
    } catch (error) {
      console.error("Error removing pharmacy:", error);
      toast.error(
        error.response?.data?.message || "Failed to remove pharmacy location"
      );
    }
  };

  // Load pharmacies on component mount
  useEffect(() => {
    fetchPharmacies();
  }, []);

  // Track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle fullscreen state changes and update icon
  useEffect(() => {
    const checkFullscreenState = () => {
      const fullscreenElement = 
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      const isCurrentlyFullscreen = !!fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);
      return isCurrentlyFullscreen;
    };

    const updateFullscreenIcon = (isFullscreen) => {
      // Force Google Maps to update the icon by triggering a resize or update
      setTimeout(() => {
        try {
          const fullscreenControls = document.querySelectorAll('.gm-fullscreen-control');
          fullscreenControls.forEach((control) => {
            if (control) {
              control.style.zIndex = '999999';
              control.style.pointerEvents = 'auto';
              
              // Find the button inside the control
              const button = control.querySelector('button');
              if (button) {
                // Force Google Maps to update by removing and re-adding the control
                // Or trigger a click event on the button to update its state
                // Actually, we'll let Google Maps handle it, but ensure it's clickable
                button.style.pointerEvents = 'auto';
                button.style.cursor = 'pointer';
                
                // Check if icon needs to be updated by looking at the button's aria-label or title
                const ariaLabel = button.getAttribute('aria-label') || button.getAttribute('title') || '';
                const shouldShowExitIcon = isFullscreen;
                const currentlyShowsExitIcon = ariaLabel.toLowerCase().includes('exit') || 
                                                ariaLabel.toLowerCase().includes('close');
                
                // If icon state is wrong, we might need to force an update
                // But Google Maps should handle this automatically, so we'll just ensure it's accessible
              }
            }
          });
          
          // Trigger map resize to ensure Google Maps updates its controls
          if (mapRef.current && window.google?.maps?.event) {
            window.google.maps.event.trigger(mapRef.current, 'resize');
          }
        } catch (error) {
          console.warn('Error updating fullscreen control:', error);
        }
      }, 150);
    };

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = checkFullscreenState();
      updateFullscreenIcon(isCurrentlyFullscreen);
    };

    // Check initial state
    const initialFullscreen = checkFullscreenState();
    updateFullscreenIcon(initialFullscreen);

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Handle fullscreen control click to prevent errors and ensure proper functionality
  useEffect(() => {
    const getFullscreenElement = () => {
      // Try to get the map's div element (what Google Maps uses)
      if (mapRef.current) {
        try {
          const mapDiv = mapRef.current.getDiv();
          if (mapDiv) return mapDiv;
        } catch (e) {
          // Fallback to container
        }
      }
      // Fallback to map container
      return document.querySelector('.map-container');
    };

    const handleFullscreenClick = (e) => {
      // Check if click is on fullscreen control button
      const target = e.target;
      const isFullscreenButton = target.closest('.gm-fullscreen-control button') || 
                                 target.closest('.gm-fullscreen-control');
      
      if (!isFullscreenButton) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Get current fullscreen state - check if map container or its children are in fullscreen
      const fullscreenElement = 
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      
      const currentFullscreen = !!fullscreenElement;
      const elementToFullscreen = getFullscreenElement();
      
      if (!elementToFullscreen) return;

      if (currentFullscreen) {
        // Exit fullscreen - only if the fullscreen element is our map or its container
        const isOurElement = fullscreenElement === elementToFullscreen || 
                            elementToFullscreen.contains(fullscreenElement) ||
                            fullscreenElement.contains(elementToFullscreen);
        
        if (isOurElement) {
          const exitFullscreen = () => {
            if (document.fullscreenElement && document.exitFullscreen) {
              return document.exitFullscreen().catch((err) => {
                // Only log if it's not a "not active" error
                if (!err.message?.includes('not active') && !err.message?.includes('Document not active')) {
                  console.warn('Error exiting fullscreen:', err);
                }
              });
            }
            if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
              return document.webkitExitFullscreen().catch((err) => {
                if (!err.message?.includes('not active') && !err.message?.includes('Document not active')) {
                  console.warn('Error exiting fullscreen:', err);
                }
              });
            }
            if (document.mozFullScreenElement && document.mozCancelFullScreen) {
              return document.mozCancelFullScreen().catch((err) => {
                if (!err.message?.includes('not active') && !err.message?.includes('Document not active')) {
                  console.warn('Error exiting fullscreen:', err);
                }
              });
            }
            if (document.msFullscreenElement && document.msExitFullscreen) {
              return document.msExitFullscreen().catch((err) => {
                if (!err.message?.includes('not active') && !err.message?.includes('Document not active')) {
                  console.warn('Error exiting fullscreen:', err);
                }
              });
            }
            return Promise.resolve();
          };

          exitFullscreen();
        }
      } else {
        // Enter fullscreen - use the map container
        const enterFullscreen = () => {
          if (elementToFullscreen.requestFullscreen) {
            return elementToFullscreen.requestFullscreen().catch((err) => {
              console.warn('Error entering fullscreen:', err);
            });
          }
          if (elementToFullscreen.webkitRequestFullscreen) {
            return elementToFullscreen.webkitRequestFullscreen().catch((err) => {
              console.warn('Error entering fullscreen:', err);
            });
          }
          if (elementToFullscreen.mozRequestFullScreen) {
            return elementToFullscreen.mozRequestFullScreen().catch((err) => {
              console.warn('Error entering fullscreen:', err);
            });
          }
          if (elementToFullscreen.msRequestFullscreen) {
            return elementToFullscreen.msRequestFullscreen().catch((err) => {
              console.warn('Error entering fullscreen:', err);
            });
          }
          return Promise.resolve();
        };

        enterFullscreen();
      }
    };

    // Add click listener with capture to intercept before Google Maps
    document.addEventListener('click', handleFullscreenClick, true);

    // Handle unhandled promise rejections - catch all fullscreen errors
    const handleUnhandledRejection = (event) => {
      const errorMessage = event.reason?.message || 
                          event.reason?.toString() || 
                          String(event.reason) || 
                          '';
      
      if (errorMessage.includes('exitFullscreen') || 
          errorMessage.includes('Document not active') ||
          errorMessage.includes('not active') ||
          errorMessage.includes('Failed to execute')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
      }
    };

    // Handle regular errors too
    const handleError = (event) => {
      const errorMessage = event.message || 
                          event.error?.message || 
                          String(event.error) || 
                          '';
      
      if (errorMessage.includes('exitFullscreen') || 
          errorMessage.includes('Document not active') ||
          errorMessage.includes('not active') ||
          errorMessage.includes('Failed to execute')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
    window.addEventListener('error', handleError, true);

    return () => {
      document.removeEventListener('click', handleFullscreenClick, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      window.removeEventListener('error', handleError, true);
    };
  }, []);

  const mapContainerStyle = {
    width: "100%",
    height: "600px",
  };

  if (!isLoaded) {
    return (
      <div className="pharmacy-import-container">
        <div className="loading-container">
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pharmacy-import-container">
      <div className="pharmacy-import-header">
        <h2>Pharmacy Data</h2>
        <div className="upload-section">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: "none" }}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" className="upload-button">
            {uploading ? "Uploading..." : "Upload CSV File"}
          </label>
          {pharmacies.length > 0 && (
            <span className="pharmacy-count">
              {pharmacies.length} pharmacy(ies) loaded
            </span>
          )}
        </div>
      </div>

      <div className="map-container">
        {loading ? (
          <div className="loading-container">
            <p>Loading pharmacies...</p>
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={(map) => {
              mapRef.current = map;
              // Ensure fullscreen control is positioned on the right side and icon state is correct
              setTimeout(() => {
                try {
                  // Check actual fullscreen state
                  const isActuallyFullscreen = !!(
                    document.fullscreenElement ||
                    document.webkitFullscreenElement ||
                    document.mozFullScreenElement ||
                    document.msFullscreenElement
                  );
                  
                  const fullscreenControls = document.querySelectorAll('.gm-fullscreen-control');
                  fullscreenControls.forEach((control) => {
                    if (control) {
                      // Only update positioning, don't interfere with event handlers
                      control.style.position = 'absolute';
                      control.style.right = '10px';
                      control.style.top = '10px';
                      control.style.left = 'auto';
                      control.style.zIndex = '999999';
                      control.style.pointerEvents = 'auto';
                      
                      // Force Google Maps to update the icon by triggering a resize
                      // This ensures the icon reflects the actual fullscreen state
                      if (window.google?.maps?.event) {
                        window.google.maps.event.trigger(map, 'resize');
                      }
                    }
                  });
                  
                  // Update our state
                  setIsFullscreen(isActuallyFullscreen);
                } catch (error) {
                  console.warn('Error positioning fullscreen control:', error);
                }
              }, 100);
              
              // Also check after a longer delay to catch any async updates
              setTimeout(() => {
                try {
                  const isActuallyFullscreen = !!(
                    document.fullscreenElement ||
                    document.webkitFullscreenElement ||
                    document.mozFullScreenElement ||
                    document.msFullscreenElement
                  );
                  setIsFullscreen(isActuallyFullscreen);
                  
                  if (window.google?.maps?.event && map) {
                    window.google.maps.event.trigger(map, 'resize');
                  }
                } catch (error) {
                  console.warn('Error updating fullscreen state:', error);
                }
              }, 500);
            }}
            options={{
              gestureHandling: "greedy",
              draggable: true,
              scrollwheel: true,
              disableDoubleClickZoom: false,
              fullscreenControl: true,
              fullscreenControlOptions: {
                position: window.google?.maps?.ControlPosition?.TOP_RIGHT ?? 3,
              },
            }}
          >
            {pharmacies.map((pharmacy) => (
              <Marker
                key={pharmacy.id}
                position={{
                  lat: pharmacy.latitude,
                  lng: pharmacy.longitude,
                }}
                onClick={() => handleMarkerClick(pharmacy)}
                title={pharmacy.pharmacy_name || "Pharmacy"}
              />
            ))}

            {selectedPharmacy && (
              <InfoWindow
                position={{
                  lat: selectedPharmacy.latitude,
                  lng: selectedPharmacy.longitude,
                }}
                onCloseClick={handleInfoWindowClose}
              >
                <div 
                  className="info-window-content"
                  style={{
                    backgroundColor: '#000',
                    color: '#fff',
                    padding: '10px',
                    borderRadius: '8px',
                    minWidth: '200px',
                    position: 'relative'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                        {selectedPharmacy.pharmacy_name || "Pharmacy"}
                      </h4>
                      <p className="coordinates-text" style={{ color: '#fff', margin: '0', fontSize: '12px', wordBreak: 'break-word' }}>
                        Lat: {selectedPharmacy.latitude.toFixed(6)}
                      </p>
                      <p className="coordinates-text" style={{ color: '#fff', margin: '4px 0 0 0', fontSize: '12px', wordBreak: 'break-word' }}>
                        Lng: {selectedPharmacy.longitude.toFixed(6)}
                      </p>
                    </div>
                    <div ref={menuRef} style={{ position: 'relative', flexShrink: 0, zIndex: 1000 }}>
                      <button
                        onClick={handleMenuToggle}
                        type="button"
                        style={{
                          backgroundColor: showMenu ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                          color: '#fff',
                          border: 'none',
                          padding: '6px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          transition: 'background-color 0.2s',
                          outline: 'none'
                        }}
                        onMouseOver={(e) => {
                          if (!showMenu) {
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!showMenu) {
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                        title="More options"
                      >
                        <FaEllipsisV />
                      </button>
                    </div>
                  </div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>

      {/* Render dropdown menu outside InfoWindow using Portal */}
      {showMenu && menuPosition.top > 0 && selectedPharmacy && createPortal(
        <div
          ref={dropdownRef}
          className="pharmacy-menu-dropdown"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '160px',
            width: 'max-content',
            zIndex: 2147483647,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            padding: '4px 0',
            display: 'block',
            visibility: 'visible',
            opacity: 1,
            pointerEvents: 'auto',
            isolation: 'isolate'
          }}
        >
          <button
            onClick={handleRemoveFromMenu}
            type="button"
            className="dropdown-menu-item"
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              color: '#000',
              border: 'none',
              padding: '8px 16px',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '400',
              transition: 'background-color 0.15s ease',
              display: 'block',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            Remove from Map
          </button>
        </div>,
        getPortalTarget()
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PharmacyImport;

